import { useEffect, useState } from "react";
import useQuery from "utils/hooks/useQuery";
import { useDispatch, useSelector } from "react-redux";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import { FILTER_TYPES_DATA } from "@shared/features/filters/constants";
import { invert } from "lodash";
import { FiltersActions, FiltersSelector, FilterType } from "@shared/types";
import {
  getConstantsMapping,
  getIsAllSelected,
  getValuesConstants,
} from "@shared/features/filters/utils";
import { getParsedQueryFilters } from "common/Filters/common/utils";

const useFiltersQuery = (
  filtersSelectors: FiltersSelector,
  filtersActions: FiltersActions,
  hideFilterTypes?: FilterType[]
) => {
  const [isFirstRender, setIsFirstRender] = useState(true);
  const dispatch = useDispatch();
  const query = useQuery();
  const { appendQuery } = useHistoryWithQuery();
  const filtersSlice = useSelector(filtersSelectors.selectFilters);
  const { config: filterConfig, filters, rawData } = filtersSlice;

  useEffect(() => {
    if (filtersSlice.loadedFilters) {
      updateFiltersFromQuery();
      setIsFirstRender(false);
      return () => {
        dispatch(filtersActions.selectAll(undefined));
      };
    }
  }, [filtersActions, filtersSlice.loadedFilters]);

  useEffect(() => {
    // It can take 1 render cycle after a page change before the Filters are updated, therefore we don't
    // update the query in the first render.
    if (!isFirstRender) {
      updateQueryFromFilters();
    }
  }, [filters, isFirstRender]);

  const convertConstantsToValues = (
    filterType: FilterType,
    constantValues: any
  ) => {
    let constantsMapping = getConstantsMapping(filterType, rawData);
    if (constantsMapping && Array.isArray(constantValues)) {
      if (filterType === FilterType.SOURCE_NAME) {
        constantsMapping = Object.values(constantsMapping).reduce(
          (acc: Record<string, number>, sourceToID) => {
            return { ...acc, ...sourceToID };
          },
          {}
        );
      }
      const reverseMapping: Record<number, string> = invert(
        constantsMapping as any
      );
      return constantValues
        .map((value) => reverseMapping[value])
        .filter((value) => !!value);
    } else {
      return constantValues;
    }
  };

  const setFilterValue = (filterType: FilterType, queryValue: any) => {
    const actionName = FILTER_TYPES_DATA[filterType].setActionName;
    const action = actionName && filtersActions[actionName];
    if (!action) return;

    let parsedValue;
    if (Array.isArray(queryValue)) {
      parsedValue = convertConstantsToValues(filterType, queryValue);
    } else if (typeof queryValue === "boolean") {
      parsedValue = queryValue;
    } else {
      parsedValue = [queryValue];
    }
    if (filterType === FilterType.TIME_FRAME) {
      parsedValue = { range: parsedValue };
    }
    dispatch(action(parsedValue));
  };

  const updateFiltersFromQuery = () => {
    const parsedQueryFiltersData = getParsedQueryFilters(
      filterConfig.filterTypes || [],
      query
    );
    Object.entries(parsedQueryFiltersData).forEach(
      ([filterType, filterValue]) => {
        if (hideFilterTypes?.includes(parseInt(filterType))) return;
        setFilterValue(parseInt(filterType), filterValue);
      }
    );
    dispatch(filtersActions.setFiltersLoadedFromQuery());
  };

  const getFilterValue = (filterType: FilterType) => {
    if (filtersSlice.loadedFilters && filterType in filters) {
      const filterData = filters[filterType];
      if (filterData && typeof filterData === "object") {
        const filterValue = filterData.value as any;
        if (filterType === FilterType.TIME_FRAME) return filterValue?.range;
        return filterValue;
      }
    }
  };

  const updateQueryFromFilters = () => {
    if (!filtersSlice.loadedFromQuery) return;
    const updatedQuery = new URLSearchParams(query.toString());
    let queryKeysToDelete: string[] = [];
    filterConfig.filterTypes?.forEach((filterType) => {
      if (hideFilterTypes?.includes(filterType)) return;
      if (getIsAllSelected(filterType, filters[filterType])) {
        queryKeysToDelete.push(FILTER_TYPES_DATA[filterType].queryKey);
      } else {
        const filterValue = getFilterValue(filterType);
        if (filterValue !== undefined) {
          updatedQuery.set(
            FILTER_TYPES_DATA[filterType].queryKey,
            JSON.stringify(
              getValuesConstants(filterValue, filterType, filtersSlice)
            )
          );
        }
      }
    });
    appendQuery(updatedQuery.toString(), queryKeysToDelete);
  };
};

export default useFiltersQuery;
